'use client';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useReport } from '@/hooks/useReport';
import { errorHandler } from '@/lib/errorHandler';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store';
import { zodResolver } from '@hookform/resolvers/zod';
import { Ellipsis } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const formSchema = z
  .object({
    reasonId: z.string().optional(),
    otherReason: z.string().optional(),
    comments: z.string().optional(),
    isOtherReason: z.boolean().optional(),
  })
  .refine((data) => data.reasonId || data.isOtherReason, {
    message: 'Please select a reason or specify another reason',
    path: ['reasonId', 'otherReason'],
  })
  .refine(
    (data) => {
      if (data.isOtherReason) {
        return !!data.otherReason;
      }
      return true;
    },
    {
      message: 'Please specify the reason for reporting this question',
      path: ['otherReason'],
    }
  );

interface QuestionActionsProps {
  questionId: string;
  questionAuthorId: string;
  className?: string;
  isActivity?: boolean;
  onHandleEditQuestion?: (id: string) => void;
  onHandleDeleteQuestion?: (id: string) => void;
}

export function QuestionActions({
  questionId,
  questionAuthorId,
  className,
  isActivity,
  onHandleEditQuestion,
  onHandleDeleteQuestion,
}: QuestionActionsProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const isUserLoggedIn = !!token;

  const isQuestionOwner = useMemo(() => {
    return user?.id === questionAuthorId;
  }, [questionAuthorId, user?.id]);

  const handleReportClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDropdownOpen(false);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDropdownOpen(false);
    onHandleDeleteQuestion?.(questionId);
  };

  const handleEditClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDropdownOpen(false);
    onHandleEditQuestion?.(questionId);
  };

  const { reportApi, getReportReasonApi } = useReport({
    loadReportReason: !!isUserLoggedIn,
  });

  const reportReasonOptions = useMemo(() => {
    return (
      getReportReasonApi.data?.data?.map((reason) => ({
        label: reason.name,
        value: reason.id,
        description: reason.description,
      })) || []
    );
  }, [getReportReasonApi.data]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reasonId: '',
      otherReason: '',
      comments: '',
      isOtherReason: false,
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    reportApi.mutate(
      {
        questionId: questionId,
        comments: values.comments,
        reasonId: values.reasonId,
        otherReason: values.isOtherReason ? values.otherReason : undefined,
      },
      {
        onSuccess: () => {
          toast.success('Reported successfully.');
          setIsDialogOpen(false);
          form.reset();
        },
        onError: (error) => {
          toast.error(errorHandler(error));
        },
      }
    );
  }
  if (isQuestionOwner && !isActivity) {
    return null;
  }

  const isDisabled = !form.watch('reasonId') && !form.watch('isOtherReason');

  return (
    <>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger
          disabled={!isUserLoggedIn}
          className={cn(
            'cursor-pointer absolute top-2 right-6 disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
          id="report-dropdown-trigger"
          aria-label="Report"
          aria-expanded={isDropdownOpen}
          aria-haspopup="true"
          aria-controls="report-dropdown-content"
        >
          <Ellipsis className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {!isActivity && !isQuestionOwner && (
            <DropdownMenuItem onClick={handleReportClick}>
              Report
            </DropdownMenuItem>
          )}

          {isActivity && (
            <>
              <DropdownMenuItem onClick={handleEditClick}>
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDeleteClick}>
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <DialogHeader>
                <DialogTitle>Report Question</DialogTitle>
              </DialogHeader>

              <ScrollArea className="max-h-[420px] overflow-y-auto w-full">
                <div className="space-y-4 p-1">
                  {reportReasonOptions.map((reason) => (
                    <div
                      key={reason.value}
                      className="flex items-center space-x-2"
                    >
                      <FormField
                        control={form.control}
                        name="reasonId"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-1">
                            <FormControl>
                              <Checkbox
                                value={reason.value}
                                checked={field.value === reason.value}
                                onCheckedChange={(checked: boolean) => {
                                  if (checked) {
                                    form.setValue('reasonId', reason.value);
                                    form.setValue('isOtherReason', false);
                                    form.setValue('otherReason', '');
                                    form.clearErrors('otherReason');
                                  } else {
                                    form.setValue('reasonId', '');
                                  }
                                }}
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>
                                <div className="flex flex-col gap-1">
                                  <label
                                    htmlFor={`reason-${reason.value}`}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  >
                                    {reason.label}
                                  </label>
                                  <p className="text-xs text-gray-500 leading-tight">
                                    {reason.description}
                                  </p>
                                </div>
                              </FormLabel>
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>
                  ))}
                </div>
              </ScrollArea>

              <FormField
                control={form.control}
                name="isOtherReason"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-1 p-1">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(checked: boolean) => {
                          field.onChange(checked);
                          setTimeout(() => {
                            if (checked) {
                              form.setFocus('otherReason');
                              form.setValue('reasonId', '');
                              form.clearErrors('reasonId');
                            } else {
                              form.clearErrors('otherReason');
                              form.setValue('otherReason', '');
                            }
                          }, 0);
                        }}
                      />
                    </FormControl>
                    <div className="space-y-1 w-full leading-none">
                      <FormLabel>
                        <FormField
                          control={form.control}
                          name="otherReason"
                          render={({ field }) => (
                            <FormItem className="w-full">
                              <FormLabel
                                htmlFor="other-reason"
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                Others
                              </FormLabel>
                              <FormControl className="w-full">
                                <Input
                                  disabled={!form.watch('isOtherReason')}
                                  className="w-full "
                                  placeholder="Please specify the reason for reporting this question"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </FormLabel>
                    </div>
                  </FormItem>
                )}
              />

              <div className="w-full">
                <FormField
                  control={form.control}
                  name="comments"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel
                        htmlFor="additional-comments"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Additional Comments
                      </FormLabel>
                      <FormControl>
                        <Input
                          id="additional-comments"
                          className="w-full"
                          placeholder="Please provide any additional comments or context"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter className="flex justify-end">
                <Button
                  variant="outline"
                  disabled={isDisabled || reportApi.isPending}
                  className="mr-2 uppercase font-bold"
                  type="submit"
                  isLoading={reportApi.isPending}
                >
                  Submit
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
